import React from 'react'
import { Button, Card, Form, Image, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Typography, message } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { listShopItemMappings, createShopItemMapping, updateShopItemMapping, deleteShopItemMapping } from '../../api/shopShopItemMap'
import { listShops } from '../../api/shops'
import { listShopTags } from '../../api/shopTags'
import ImageUploadField from '../../components/ImageUploadField'
import { ADMIN_IMAGE_FIELD_PLACEHOLDER, resolveAdminImageUrl } from '../../utils/adminImageUpload'

export default function ShopShopItemMapsAdmin() {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const [shops, setShops] = React.useState([])
  const [allCategories, setAllCategories] = React.useState([])

  const [createOpen, setCreateOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [selected, setSelected] = React.useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()
  const createImageRef = React.useRef(null)
  const editImageRef = React.useRef(null)

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const [maps, shopsList, catList] = await Promise.all([
        listShopItemMappings(),
        listShops(),
        listShopTags(),
      ])
      setData(maps || [])
      setShops(shopsList || [])
      setAllCategories(catList || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^\s*"?|"?\s*$/g, '')
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const imageUrl = await resolveAdminImageUrl(values, createImageRef)
      const payload = {
        shopId: values.shopId,
        nameAr: values.nameAr || undefined,
        imageUrl,
        itemTags: values.itemTags || undefined,
        sellPrice: values.sellPrice != null ? String(values.sellPrice) : undefined,
        sellPriceBeforeDiscount: values.sellPriceBeforeDiscount != null ? String(values.sellPriceBeforeDiscount) : undefined,
        available: values.available ?? undefined,
        bulkMinQuantity: values.bulkMinQuantity != null ? Number(values.bulkMinQuantity) : undefined,
        bulkSellPrice: values.bulkSellPrice != null ? String(values.bulkSellPrice) : undefined,
        buyPrice: values.buyPrice != null ? String(values.buyPrice) : undefined,
        mainCategoryIds: values.mainCategoryIds?.length ? values.mainCategoryIds : undefined,
      }
      await createShopItemMapping(payload)
      message.success('تم الإنشاء')
      setCreateOpen(false)
      createForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  function openEdit(record) {
    setSelected(record)
    editForm.setFieldsValue({
      shopId: record.shop?.id,
      nameAr: record.nameAr || record.item?.nameAr,
      imageUrl: record.imageUrl || record.item?.imageUrl,
      itemTags: record.itemTags,
      mainCategoryIds: Array.isArray(record.mainCategories) ? record.mainCategories.map(c => c.id) : [],
      sellPrice: Number(record.sellPrice ?? 0),
      sellPriceBeforeDiscount: record.sellPriceBeforeDiscount != null ? Number(record.sellPriceBeforeDiscount) : undefined,
      available: record.available !== false,
      bulkMinQuantity: record.bulkMinQuantity != null ? Number(record.bulkMinQuantity) : undefined,
      bulkSellPrice: record.bulkSellPrice != null ? Number(record.bulkSellPrice) : undefined,
      buyPrice: record.buyPrice != null ? Number(record.buyPrice) : undefined,
    })
    setEditOpen(true)
  }

  async function onEdit(values) {
    if (!selected) return
    setEditing(true)
    try {
      const payload = {}
      const sShopId = selected.shop?.id
      if (values.shopId !== sShopId) payload.shopId = values.shopId

      function numOrUndef(x) { return x == null ? undefined : Number(x) }

      if ((values.nameAr || '') !== (selected.nameAr || selected.item?.nameAr || '')) payload.nameAr = values.nameAr || undefined

      const imageUrl = await resolveAdminImageUrl(values, editImageRef)
      if ((imageUrl || '') !== (selected.imageUrl || selected.item?.imageUrl || '')) payload.imageUrl = imageUrl || undefined
      if ((values.itemTags || '') !== (selected.itemTags || '')) payload.itemTags = values.itemTags || undefined

      const currentCatIds = Array.isArray(selected.mainCategories) ? selected.mainCategories.map(c => c.id).sort().join(',') : ''
      const newCatIds = Array.isArray(values.mainCategoryIds) ? [...values.mainCategoryIds].sort().join(',') : ''
      if (newCatIds !== currentCatIds) payload.mainCategoryIds = values.mainCategoryIds || []

      if (Number(values.sellPrice) !== Number(selected.sellPrice)) payload.sellPrice = String(values.sellPrice)
      const selBefore = selected.sellPriceBeforeDiscount == null ? undefined : Number(selected.sellPriceBeforeDiscount)
      if (numOrUndef(values.sellPriceBeforeDiscount) !== selBefore) payload.sellPriceBeforeDiscount = values.sellPriceBeforeDiscount == null ? undefined : String(values.sellPriceBeforeDiscount)
      if (!!values.available !== !!selected.available) payload.available = !!values.available
      if (numOrUndef(values.bulkMinQuantity) !== numOrUndef(selected.bulkMinQuantity)) payload.bulkMinQuantity = numOrUndef(values.bulkMinQuantity)
      const selBulkSell = numOrUndef(selected.bulkSellPrice)
      if (numOrUndef(values.bulkSellPrice) !== selBulkSell) payload.bulkSellPrice = values.bulkSellPrice == null ? undefined : String(values.bulkSellPrice)

      const selBuy = selected.buyPrice == null ? undefined : Number(selected.buyPrice)
      if (numOrUndef(values.buyPrice) !== selBuy) payload.buyPrice = values.buyPrice == null ? undefined : String(values.buyPrice)

      if (Object.keys(payload).length === 0) {
        message.info('لا تغييرات للحفظ')
      } else {
        await updateShopItemMapping(selected.id, payload)
        message.success('تم التحديث')
      }
      editImageRef.current?.clearPendingFile?.()
      setEditOpen(false)
      setSelected(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setEditing(false)
    }
  }

  async function onDelete(id) {
    try {
      await deleteShopItemMapping(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  const filterDropdownFor = (placeholder, getValue, matchValue) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
        <Input
          placeholder={placeholder}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 160, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" size="small" onClick={() => confirm()}>تطبيق</Button>
          <Button size="small" onClick={() => { clearFilters?.(); confirm() }}>مسح</Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => {
      const val = (value || '').trim().toLowerCase()
      if (!val) return true
      const cell = getValue(record)
      return matchValue ? matchValue(cell, val) : String(cell ?? '').toLowerCase().includes(val)
    },
  })

  const columns = [
    {
      title: 'رقم',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      ...filterDropdownFor('تصفية بالرقم', r => r?.id, (cell, val) => {
        const num = Number(val)
        return Number.isFinite(num) ? cell === num : String(cell ?? '').includes(val)
      }),
    },
    {
      title: 'المتجر',
      key: 'shop',
      render: (_, r) => r?.shop?.nameAr || r?.shop?.name || '',
      ...filterDropdownFor('تصفية بالمتجر', r => r?.shop?.nameAr || r?.shop?.name || ''),
    },
    {
      title: 'العنصر',
      key: 'item',
      render: (_, r) => r?.nameAr || r?.item?.nameAr || r?.item?.name || '',
      ...filterDropdownFor('تصفية بالاسم', r => r?.nameAr || r?.item?.nameAr || r?.item?.name || ''),
    },
    {
      title: 'صورة', key: 'item_image_url', width: 110, render: (_, r) => (
        r?.imageUrl ? (
          <div style={{ width: 64, height: 64, overflow: 'hidden', background: '#fafafa', borderRadius: 4 }}>
            <Image
              src={`https://images.leen-go.com/${r.imageUrl}`}
              alt={r?.nameAr || r?.item?.nameAr || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        ) : ''
      )
    },
    { title: 'الوسوم', dataIndex: 'itemTags', key: 'itemTags', width: 200 },
    { title: 'سعر البيع', dataIndex: 'sellPrice', key: 'sellPrice', width: 120 },
    { title: 'سعر الشراء', dataIndex: 'buyPrice', key: 'buyPrice', width: 120 },
    { title: 'قبل الخصم', dataIndex: 'sellPriceBeforeDiscount', key: 'sellPriceBeforeDiscount', width: 150 },
    { title: 'متوفر', dataIndex: 'available', key: 'available', width: 120, render: (v) => v ? 'نعم' : 'لا' },
    { title: 'الحد الأدنى للكمية', dataIndex: 'bulkMinQuantity', key: 'bulkMinQuantity', width: 130 },
    { title: 'سعر الجملة', dataIndex: 'bulkSellPrice', key: 'bulkSellPrice', width: 120 },
    {
      title: '', key: 'actions', width: 220,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEdit(record)}>تعديل</Button>
          <Popconfirm title="حذف هذا الربط؟" onConfirm={() => onDelete(record.id)}>
            <Button danger>حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 16px' }}>
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>تسعير عناصر المتجر</Typography.Title>
        <Button type="primary" onClick={() => setCreateOpen(true)}>ربط جديد</Button>
      </Space>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="إنشاء ربط"
        open={createOpen}
        onCancel={() => {
          createImageRef.current?.clearPendingFile?.()
          setCreateOpen(false)
        }}
        okText="إنشاء"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate}>
          <Form.Item label="المتجر" name="shopId" rules={[{ required: true, message: 'يرجى اختيار متجر' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))}
            />
          </Form.Item>
          <Form.Item label="اسم العنصر" name="nameAr" rules={[{ required: true, message: 'يرجى إدخال اسم العنصر' }]}>
            <input type="text" className="ant-input" />
          </Form.Item>
          <Form.Item label="رابط الصورة" name="imageUrl">
            <ImageUploadField ref={createImageRef} placeholder={ADMIN_IMAGE_FIELD_PLACEHOLDER} />
          </Form.Item>
          <Form.Item label="وسوم (مفصولة بفواصل)" name="itemTags">
            <input type="text" className="ant-input" placeholder="خضار,فواكه" />
          </Form.Item>
          <Form.Item label="التصنيفات الرئيسية" name="mainCategoryIds">
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="اختر التصنيفات (اختياري)"
              options={allCategories.map(c => ({ label: c.tagAr, value: c.id }))}
            />
          </Form.Item>
          <Form.Item label="سعر البيع" name="sellPrice" rules={[{ required: true, message: 'يرجى إدخال السعر' }]}>
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="سعر الشراء" name="buyPrice">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="قبل الخصم" name="sellPriceBeforeDiscount">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="متوفر" name="available" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item label="الحد الأدنى للكمية" name="bulkMinQuantity">
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="سعر الجملة" name="bulkSellPrice">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تعديل ربط"
        open={editOpen}
        onCancel={() => {
          editImageRef.current?.clearPendingFile?.()
          setEditOpen(false)
        }}
        okText="حفظ"
        confirmLoading={editing}
        onOk={() => editForm.submit()}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item label="المتجر" name="shopId">
            <Select showSearch optionFilterProp="label" options={shops.map(s => ({ label: s.nameAr || s.name, value: s.id }))} />
          </Form.Item>
          <Form.Item label="اسم العنصر" name="nameAr">
            <input type="text" className="ant-input" />
          </Form.Item>
          <Form.Item label="رابط الصورة" name="imageUrl">
            <ImageUploadField ref={editImageRef} placeholder={ADMIN_IMAGE_FIELD_PLACEHOLDER} />
          </Form.Item>
          <Form.Item label="وسوم (مفصولة بفواصل)" name="itemTags">
            <input type="text" className="ant-input" placeholder="خضار,فواكه" />
          </Form.Item>
          <Form.Item label="التصنيفات الرئيسية" name="mainCategoryIds">
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="اختر التصنيفات (اختياري)"
              options={allCategories.map(c => ({ label: c.tagAr, value: c.id }))}
            />
          </Form.Item>
          <Form.Item label="سعر البيع" name="sellPrice">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="سعر الشراء" name="buyPrice">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="قبل الخصم" name="sellPriceBeforeDiscount">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="متوفر" name="available" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="الحد الأدنى للكمية" name="bulkMinQuantity">
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="سعر الجملة" name="bulkSellPrice">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
